import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useZoneDetail, useZones } from './hooks/useMaintenanceZones';
import { Button, Badge, Card, Avatar, FeaturePlaceholder } from '@/components/ui';
import { ZoneFormModal } from './components/ZoneFormModal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { AuthButton } from '@/core/permissions/AuthButton';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  active: 'bg-emerald-100 text-emerald-600',
  maintenance: 'bg-blue-100 text-blue-600',
  inactive: 'bg-amber-100 text-amber-600',
  archived: 'bg-slate-200 text-slate-500',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản thảo',
  active: 'Hoạt động',
  maintenance: 'Bảo trì',
  inactive: 'Ngưng',
  archived: 'Lưu trữ',
};

export default function ZoneDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const { data: zone, isLoading, error } = useZoneDetail(id);
  const { deleteZone, isDeleting } = useZones(companyId);

  const [activeTab, setActiveTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!zone) return;
    try {
      await deleteZone(zone.id);
      toast.success('Đã xóa khu vực thành công');
      navigate('/maintenance/zones');
    } catch (error: any) {
      toast.error('Lỗi khi xóa: ' + error.message);
    }
  };

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải thông tin khu vực...</div>;
  if (!zone) return <div className="p-12 text-center text-red-500 font-bold">Không tìm thấy dữ liệu khu vực</div>;

  const tabs = [
    { value: 'overview', label: 'Tổng quan' },
    { value: 'assets', label: 'Tài sản' },
    { value: 'teams', label: 'Tổ đội' },
    { value: 'work', label: 'Công việc' },
    { value: 'performance', label: 'Hiệu suất' },
    { value: 'map', label: 'Bản đồ' },
    { value: 'logs', label: 'Nhật ký' },
  ];

  return (
    <PageDetailLayout
      title={zone.zone_name}
      description={
        <div className="flex items-center gap-3 mt-1">
          <Badge className={`${statusColors[zone.status_code] || 'bg-slate-100 text-slate-600'} border-transparent text-[10px] font-bold `}>
            {statusLabels[zone.status_code] || zone.status_code}
          </Badge>
          <span className="text-sm text-slate-400 font-mono">{zone.zone_code}</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500 flex items-center gap-1">
            <i className="ri-building-line" />
            {zone.project?.name || 'N/A'}
          </span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-emerald-600 font-bold">{zone.area_m2?.toLocaleString()} m²</span>
        </div>
      }
      breadcrumbs={[
        { label: 'Khu vực bảo dưỡng', path: '/maintenance/zones' },
        { label: zone.zone_code }
      ]}
      headerActions={
        <div className="flex gap-2">
          <AuthButton module="maintenance" action="update" variant="outline" icon="ri-edit-line" onClick={() => setShowEdit(true)}>
            Chỉnh sửa
          </AuthButton>
          <AuthButton
            module="maintenance"
            action="delete"
            variant="ghost"
            className="text-red-500 hover:bg-red-50"
            icon="ri-delete-bin-line"
            onClick={() => setShowDelete(true)}
          />
        </div>
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {activeTab === 'overview' && (
            <>
              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <i className="ri-information-line text-os-primary" />
                  Thông tin vận hành
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-y-6 gap-x-4">
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Loại hình</p>
                    <p className="text-sm font-semibold text-slate-700 capitalize">{zone.zone_type}</p>
                  </div>
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Diện tích</p>
                    <p className="text-sm font-bold text-slate-900">{zone.area_m2?.toLocaleString()} m²</p>
                  </div>
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Tổ trưởng phụ trách</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Avatar src={zone.manager?.avatar_url || undefined} fallback={zone.manager?.full_name?.substring(0, 2).toUpperCase() || 'FM'} size="xs" />
                      <span className="text-sm font-semibold text-slate-700">{zone.manager?.full_name || 'Chưa gán'}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Dự án</p>
                    <p className="text-sm font-semibold text-slate-700">{zone.project?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Hợp đồng</p>
                    <p className="text-sm font-semibold text-slate-700">{zone.contract?.contract_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-[10px]  text-slate-400 font-bold mb-1">Khu vực cha</p>
                    <p className="text-sm font-semibold text-slate-700">{zone.parent_zone?.zone_name || '---'}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <i className="ri-text-snippet text-os-primary" />
                  Mô tả chi tiết
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {zone.description || 'Chưa có mô tả chi tiết cho khu vực này.'}
                </p>
              </Card>
            </>
          )}

          {activeTab !== 'overview' && (
            <FeaturePlaceholder
              title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
              description="Phần này đang được tích hợp dữ liệu chi tiết và bản đồ GIS."
            />
          )}
        </div>

        <div className="space-y-6">
          <Card className="p-6">
            <h4 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <i className="ri-map-pin-user-line text-slate-400" />
              Tổ đội tại chỗ
            </h4>
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-os-primary/10 flex items-center justify-center text-os-primary">
                  <i className="ri-team-line text-xl" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-900">Chưa có tổ đội gán</p>
                  <p className="text-[10px] text-slate-500">Gán tổ đội để bắt đầu công việc</p>
                </div>
              </div>
              <div className="pt-4 border-t border-slate-50">
                <p className="text-[10px]  text-slate-400 font-bold mb-2">Hành động nhanh</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" icon="ri-user-add-line" className="text-[10px]">Gán Foreman</Button>
                  <Button variant="outline" size="sm" icon="ri-group-line" className="text-[10px]">Thêm Team</Button>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-slate-900 text-white overflow-hidden relative">
            <div className="relative z-10">
              <h4 className="font-bold mb-2 flex items-center gap-2">
                <i className="ri-radar-line text-emerald-400" />
                Dữ liệu GIS
              </h4>
              <p className="text-[10px] text-slate-400 mb-4 italic">Zone đã được vẽ ranh giới (Polygon)</p>
              <Button variant="outline" size="sm" className="bg-white/10 border-white/20 text-white hover:bg-white/20 w-full">Xem ranh giới trên Map</Button>
            </div>
            <div className="absolute -right-4 -bottom-4 text-white/5 text-8xl rotate-12">
              <i className="ri-map-2-line" />
            </div>
          </Card>
        </div>
      </div>

      <ZoneFormModal
        isOpen={showEdit}
        zone={zone}
        companyId={companyId}
        onClose={() => setShowEdit(false)}
      />

      <ConfirmDialog
        isOpen={showDelete}
        title="Xác nhận xóa khu vực"
        message={`Bạn có chắc chắn muốn xóa khu vực "${zone.zone_name}"? Mọi dữ liệu liên quan đến tài sản và hiệu suất sẽ bị xóa vĩnh viễn.`}
        confirmText="Xác nhận xóa"
        isDestructive={true}
        isLoading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onClose={() => setShowDelete(false)}
      />
    </PageDetailLayout>
  );
}
